/**
 * Game — bootstraps Pixi, loads data, starts a stage, runs the main loop.
 *
 * The loop is frame-rate independent: we compute a capped dt in seconds and
 * pass it to systems. Pixi's ticker just triggers the step.
 *
 * Everything cross-cutting (save ticking, time-slow FX, screen shake) lives
 * here because it applies regardless of stage.
 */

import { Application, Container } from 'pixi.js';
import { World } from './ECS';
import { loadGameData, type GameData } from './AssetLoader';
import { saveManager } from './SaveManager';
import { installMobileViewport } from '@/ui/MobileViewport';
import { stageManager, type StageContext } from './StageManager';
import { bus } from './EventBus';
import { CellStage } from '@/stages/cell/CellStage';
import { MulticellStage } from '@/stages/multicell/MulticellStage';
import { CambrianStage } from '@/stages/cambrian/CambrianStage';
import { player } from '@/systems/PlayerState';
import { AudioManager } from '@/systems/AudioManager';
import { showStartMenu } from '@/ui/StartMenu';

export class Game {
  app: Application;
  world: World;
  data!: GameData;
  worldLayer: Container;
  fxLayer: Container;
  uiLayer: Container;
  audio: AudioManager;

  private lastTime = performance.now();
  private timeSlowFactor = 1;
  private timeSlowRemaining = 0;
  private shakeIntensity = 0;
  private shakeRemaining = 0;
  private paused = false;

  /** The #app box the canvas lives in — what we size the renderer to. */
  private mount: HTMLElement | null = null;

  constructor() {
    this.app = new Application();
    this.world = new World();
    this.worldLayer = new Container();
    this.fxLayer = new Container();
    this.uiLayer = new Container();
    this.audio = new AudioManager();
  }

  /** Size the renderer to the part of the page the player can actually
   *  see, and tell the current stage about it.
   *
   *  The measuring stick is #app itself (fixed, 100dvh) rather than
   *  window.innerHeight or visualViewport: it is the box the canvas is
   *  drawn into, so canvas pixels and CSS pixels line up 1:1 and a touch
   *  at the bottom of the screen lands on the bottom of the world. */
  private fitToViewport(): void {
    const box = this.mount?.getBoundingClientRect();
    const vv = window.visualViewport;
    const w = Math.max(1, Math.round(box?.width || vv?.width || window.innerWidth));
    const h = Math.max(1, Math.round(box?.height || vv?.height || window.innerHeight));
    if (this.app.screen.width !== w || this.app.screen.height !== h) {
      this.app.renderer.resize(w, h);
    }
    const canvas = this.app.canvas as HTMLCanvasElement;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    stageManager.resize(w, h);
  }

  async init(mount: HTMLElement): Promise<void> {
    // Render at full device pixel density so the canvas stays crisp on
    // high-DPR phones (iPhones are DPR=3; capping at 2 made mobile look
    // like it was drawn at ⅔ resolution). autoDensity handles the CSS
    // size vs backbuffer math.
    await this.app.init({
      // Pin the renderer. Auto-detect tries WebGPU first and its availability
      // varies run to run; a renderer built before the mesh pipe registered
      // has no 'mesh' pipe and throws mid-game the first time a Mesh takes
      // the update-only render path (seen as a random freeze a few seconds
      // into Stage 3). WebGL is what every shader here was written against.
      preference: 'webgl',
      // NOTE: no resizeTo — we size to the *visual* viewport ourselves
      // (see fitToViewport). On phones the layout viewport is taller than
      // what you can see, so resizeTo:window puts the bottom of the world
      // behind the browser's URL bar, out of reach.
      background: 0x01030a,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });
    this.mount = mount;
    mount.appendChild(this.app.canvas);
    this.fitToViewport();
    // Phones: no browser zoom, pinch drives the camera, landscape only.
    installMobileViewport(this.app.canvas as HTMLCanvasElement);

    this.app.stage.addChild(this.worldLayer);
    this.app.stage.addChild(this.fxLayer);
    this.app.stage.addChild(this.uiLayer);

    this.data = await loadGameData();

    // register all stages here — Phase 2 will add more
    stageManager.register('cell', () => new CellStage());
    stageManager.register('multicell', () => new MulticellStage());
    stageManager.register('cambrian', () => new CambrianStage());
    // Prestige progression: each entry says "after FROM, the player advances
    // to TO". Phase 3 extends the chain: cell -> multicell -> cambrian.
    stageManager.setProgression([
      ['cell', 'multicell'],
      ['multicell', 'cambrian'],
    ]);

    const ctx: StageContext = {
      app: this.app,
      world: this.world,
      data: this.data,
      save: saveManager,
      worldLayer: this.worldLayer,
      fxLayer: this.fxLayer,
      uiLayer: this.uiLayer,
      screen: { width: this.app.screen.width, height: this.app.screen.height },
    };

    // Start-game menu blocks stage start until the user picks Continue or
    // New Game. Import/export happen inside the menu without dismissing it.
    // The loading placeholder is hidden before the menu appears so the intro
    // animation isn't covered.
    const loadingEl = document.getElementById('loading');
    if (loadingEl) loadingEl.classList.add('hidden');
    await showStartMenu();

    // Resume the stage the save says we're on (so refreshing the tab
    // mid-stage doesn't drop us back to Phase 1). Default to 'cell' for
    // fresh saves or unknown ids. The Stage Select buttons in StartMenu
    // already overwrite getRun().stageId before the menu dismisses.
    const registered = new Set(['cell', 'multicell', 'cambrian']);
    const savedStageId = saveManager.getRun().stageId;
    const startId = registered.has(savedStageId) ? savedStageId : 'cell';
    await stageManager.start(startId, ctx);

    // Wire the stage-complete "Continue" hand-off. The UI emits this
    // when the player clicks Continue; we consume the carryover and
    // advance to the next stage. Death-respawns still go through the
    // stage's own restart path.
    bus.on('stage:advance', () => {
      stageManager.advance().catch(err => console.warn('[Game] stage advance failed', err));
    });

    // Defer to next frame so Pixi's internal resizeTo:window handler
    // runs first and app.screen reflects the new orientation/size. Without
    // this, a mobile landscape rotation can fire our handler with stale
    // dimensions and leave the zoom pivot off-center.
    const refit = (): void => { requestAnimationFrame(() => this.fitToViewport()); };
    window.addEventListener('resize', refit);
    window.addEventListener('orientationchange', refit);
    // Mobile browsers grow/shrink the visible area as the URL bar hides;
    // the visual viewport is the only source that tracks it.
    window.visualViewport?.addEventListener('resize', refit);
    window.visualViewport?.addEventListener('scroll', refit);

    // global FX listeners
    bus.on('fx:timeSlow', ({ factor, duration }) => {
      this.timeSlowFactor = factor;
      this.timeSlowRemaining = duration;
    });
    bus.on('fx:screenShake', ({ intensity, duration }) => {
      this.shakeIntensity = Math.max(this.shakeIntensity, intensity);
      this.shakeRemaining = Math.max(this.shakeRemaining, duration);
    });

    // Pause wiring — menus and ESC emit these; the main loop freezes the
    // stage/save while paused. DOM clicks still work since the browser keeps
    // firing events independently of Pixi's ticker.
    bus.on('game:paused', () => { this.paused = true; this.lastTime = performance.now(); });
    bus.on('game:resumed', () => { this.paused = false; this.lastTime = performance.now(); });

    // Dev cheat: type "food" at normal typing speed (≤600ms between keys)
    // to trigger a stage-scaled biomass bolus. The gap check keeps the
    // sequence from being mashed with a held key or stumbled onto by
    // slow deliberate presses.
    const CHEAT_PHASE1_DELTA = 5_000;
    const CHEAT_PHASE2_DELTA = 50_000;
    const CHEAT_WORD = 'food';
    const CHEAT_MAX_GAP_MS = 600;
    let cheatBuf = '';
    let cheatLastTs = 0;
    window.addEventListener('keydown', (e) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key.length !== 1) return;
      const now = performance.now();
      if (now - cheatLastTs > CHEAT_MAX_GAP_MS) cheatBuf = '';
      cheatLastTs = now;
      cheatBuf = (cheatBuf + e.key.toLowerCase()).slice(-CHEAT_WORD.length);
      if (cheatBuf !== CHEAT_WORD) return;
      cheatBuf = '';
      const stageId = saveManager.getRun().stageId;
      const delta = stageId === 'cell' ? CHEAT_PHASE1_DELTA : CHEAT_PHASE2_DELTA;
      player.addBiomass(delta);
      saveManager.getMeta().scoreProgress.unranked = true;
      console.warn(`[cheat] +${delta} biomass`);
    });

    // Middle-mouse pause toggle: emits the same pause/resume events ESC
    // uses, so it freezes the game loop in both stages. preventDefault
    // suppresses the browser's autoscroll cursor on middle-button down.
    window.addEventListener('mousedown', (e) => {
      if (e.button !== 1) return;
      e.preventDefault();
      bus.emit(this.paused ? 'game:resumed' : 'game:paused', undefined);
    });

    this.app.ticker.add(() => this.tick());
  }

  private tick(): void {
    const now = performance.now();
    let dt = (now - this.lastTime) / 1000;
    this.lastTime = now;
    if (dt > 0.1) dt = 0.1; // avoid huge steps after tab switch

    // Hard pause: don't advance stage clock, physics, or save tick. The
    // resume handler resets lastTime so dt doesn't spike on the first
    // unpaused frame.
    if (this.paused) return;

    // time-slow FX
    if (this.timeSlowRemaining > 0) {
      this.timeSlowRemaining -= dt;
      dt *= this.timeSlowFactor;
      if (this.timeSlowRemaining <= 0) this.timeSlowFactor = 1;
    }

    // screen shake
    if (this.shakeRemaining > 0) {
      this.shakeRemaining -= dt;
      const i = this.shakeIntensity * (this.shakeRemaining / 0.35);
      this.worldLayer.position.set((Math.random() - 0.5) * i, (Math.random() - 0.5) * i);
      this.fxLayer.position.set(this.worldLayer.x, this.worldLayer.y);
      if (this.shakeRemaining <= 0) {
        this.shakeIntensity = 0;
        this.worldLayer.position.set(0, 0);
        this.fxLayer.position.set(0, 0);
      }
    }

    stageManager.update(dt);
    saveManager.tick(dt);
  }
}
